import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { ApiError } from '@/api/client';
import type { AssignmentState, AssignmentWithArticle } from '@/api/curate';
import { ComingSoon } from '@/components/ui/ComingSoon';
import { CurateIcon } from '@/components/ui/Icon';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCurateStatus, useMyAssignments } from '@/hooks/api/useCurate';
import { CurateCover } from './CurateCover';
import { REASON_ACCENT } from './coverArt';
import { CurateReaderModal } from './CurateReaderModal';
import styles from './CuratePage.module.css';

/**
 * Curate · Personalized Training.
 *
 * The signed-in provider's assigned articles, grouped by where they stand:
 *
 *   Continue   the most recently touched in-progress article, promoted to a
 *              full-bleed hero with a one-tap Resume. Any further in-progress
 *              items follow as ordinary cards.
 *   Up next    unstarted articles, soonest due date first.
 *   Completed  compact rows with the score, collapsible so a long history
 *              stops crowding the outstanding work. Expired items live here too.
 *
 * A summary strip above the groups answers "where am I overall", and filter
 * chips narrow the list client-side (the list is already in memory; a second
 * request per chip would buy nothing).
 *
 * Four non-content states, all of which are normal rather than exceptional:
 *
 *   - Feature off — the tenant has no Curate tables. `GET /curate/status`
 *     answers 200 with `enabled: false` precisely so this can render the
 *     existing Coming Soon placeholder instead of an error. Every other Curate
 *     route would answer 503, so the assignment query stays disabled entirely.
 *   - No NPI — the account carries no `extn.npi` claim, so it has no
 *     assignments by definition and the API answers 403 NPI_NOT_CONFIGURED.
 *   - No assignments — the feature is on and the provider is scoped, but
 *     nothing has been assigned yet.
 *   - Load failure — anything else.
 */
export function CuratePage() {
  const status = useCurateStatus();
  const enabled = status.data?.enabled === true;

  const assignments = useMyAssignments(undefined, { enabled });
  const [active, setActive] = useState<AssignmentWithArticle | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [showCompleted, setShowCompleted] = useState(true);

  const groups = useMemo(() => groupAssignments(assignments.data ?? []), [assignments.data]);

  /* Feature not enabled for this tenant ----------------------------------- */
  if (status.isSuccess && !enabled) {
    return (
      <ComingSoon
        title="Curate"
        subtitle="Personalized Training"
        icon={CurateIcon}
        heading="Curate is on the way"
        body="A personalized training library tailored to your performance, with case studies and learning modules prioritized by where they can make the greatest impact. Check back soon to start building your plan."
      />
    );
  }

  const npiMissing = assignments.error instanceof ApiError && assignments.error.status === 403;

  const total = groups.all.length;
  const showContinue = filter === 'all' || filter === 'in_progress';
  const showUpNext = filter === 'all' || filter === 'new';
  const showDone = filter === 'all' || filter === 'completed';
  const [hero, ...moreInProgress] = groups.inProgress;

  return (
    <section className={styles.page} aria-labelledby="curate-heading">
      <header className={styles.titleRow}>
        <div>
          <p className={styles.eyebrow}>Personalized Training</p>
          <h1 id="curate-heading" className={styles.title}>
            Curate
          </h1>
        </div>
      </header>

      {(status.isLoading || assignments.isLoading) && (
        <div aria-busy="true" aria-label="Loading your modules" className={styles.loading}>
          <Skeleton variant="block" height={84} />
          <Skeleton variant="block" height={208} />
          <Skeleton variant="block" height={100} />
          <Skeleton variant="block" height={100} />
        </div>
      )}

      {npiMissing && (
        <div className={styles.notice} role="status">
          <strong className={styles.noticeTitle}>Your NPI isn&apos;t configured</strong>
          <p className={styles.noticeBody}>
            Curate assignments are tied to your provider NPI, so nothing can be shown until your
            account is linked. Please contact your administrator.
          </p>
        </div>
      )}

      {assignments.isError && !npiMissing && (
        <div className={styles.notice} role="alert">
          <strong className={styles.noticeTitle}>Your modules couldn&apos;t be loaded</strong>
          <p className={styles.noticeBody}>
            {(assignments.error as Error)?.message ?? 'Please try again.'}
          </p>
          <button type="button" className={styles.retry} onClick={() => assignments.refetch()}>
            Try again
          </button>
        </div>
      )}

      {assignments.isSuccess && total === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <CurateIcon width={28} height={28} />
          </span>
          <h2 className={styles.emptyTitle}>No modules assigned yet</h2>
          <p className={styles.emptyBody}>
            When training is assigned to you it will appear here, prioritized by where it can make
            the greatest impact on your performance.
          </p>
        </div>
      )}

      {total > 0 && (
        <>
          <SummaryStrip groups={groups} />

          <div className={styles.chips} role="group" aria-label="Filter modules">
            {FILTERS.map(({ key, label }) => {
              const count = key === 'all' ? total : groups.counts[key];
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.chip} ${filter === key ? styles.chipOn : ''}`}
                  aria-pressed={filter === key}
                  onClick={() => setFilter(key)}
                >
                  {label}
                  <span className={styles.chipCount}>{count}</span>
                </button>
              );
            })}
          </div>

          {showContinue && hero && (
            <>
              <SectionHeading>Continue</SectionHeading>
              <HeroCard assignment={hero} onOpen={() => setActive(hero)} />
              {moreInProgress.length > 0 && (
                <ul className={styles.cardGrid}>
                  {moreInProgress.map((a) => (
                    <li key={a.assignmentId}>
                      <AssignmentCard assignment={a} onOpen={() => setActive(a)} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {showUpNext && groups.upNext.length > 0 && (
            <>
              <SectionHeading count={groups.upNext.length}>Up next</SectionHeading>
              <ul className={styles.cardGrid}>
                {groups.upNext.map((a) => (
                  <li key={a.assignmentId}>
                    <AssignmentCard assignment={a} onOpen={() => setActive(a)} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {showDone && groups.done.length > 0 && (
            <>
              <SectionHeading
                count={groups.done.length}
                action={
                  <button
                    type="button"
                    className={styles.sectionAction}
                    aria-expanded={showCompleted}
                    onClick={() => setShowCompleted((v) => !v)}
                  >
                    {showCompleted ? 'Hide' : 'Show'}
                  </button>
                }
              >
                Completed
              </SectionHeading>
              {showCompleted && (
                <ul className={styles.rowGrid}>
                  {groups.done.map((a) => (
                    <li key={a.assignmentId}>
                      <CompletedRow assignment={a} onOpen={() => setActive(a)} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {filter !== 'all' && groups.counts[filter] === 0 && (
            <p className={styles.filterEmpty}>Nothing {FILTER_EMPTY[filter]}.</p>
          )}
        </>
      )}

      <CurateReaderModal assignment={active} onClose={() => setActive(null)} />
    </section>
  );
}

/* Grouping and derived values
 * -------------------------------------------------------------------------- */

type Filter = 'all' | 'new' | 'in_progress' | 'completed';

const FILTERS: ReadonlyArray<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'Up next' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
];

const FILTER_EMPTY: Record<Exclude<Filter, 'all'>, string> = {
  new: 'waiting to be started',
  in_progress: 'in progress right now',
  completed: 'completed yet',
};

/** Rough reading pace used for every "N min" estimate on the page. */
const MINUTES_PER_PAGE = 2;

interface Groups {
  all: AssignmentWithArticle[];
  inProgress: AssignmentWithArticle[];
  upNext: AssignmentWithArticle[];
  done: AssignmentWithArticle[];
  counts: Record<Exclude<Filter, 'all'>, number>;
}

function groupAssignments(list: AssignmentWithArticle[]): Groups {
  const inProgress = list
    .filter((a) => a.state === 'in_progress')
    // Most recently touched first, so the hero is the one the learner was
    // actually reading. Falls back to the API's assigned_at DESC ordering.
    .sort((a, b) => lastTouched(b) - lastTouched(a));
  const upNext = list
    .filter((a) => a.state === 'new')
    // Soonest due first; undated items keep API order at the end.
    .sort((a, b) => dueMillis(a) - dueMillis(b));
  const done = list.filter((a) => a.state === 'completed' || a.state === 'expired');

  return {
    all: list,
    inProgress,
    upNext,
    done,
    counts: { new: upNext.length, in_progress: inProgress.length, completed: done.length },
  };
}

const lastTouched = (a: AssignmentWithArticle): number => {
  const stamp = a.progress?.lastInteractionAt ?? a.progress?.lastOpenedAt ?? a.startedAt;
  return stamp ? Date.parse(stamp) : 0;
};

const dueMillis = (a: AssignmentWithArticle): number =>
  a.dueDate ? Date.parse(a.dueDate) : Number.POSITIVE_INFINITY;

/** Pages the learner has not reached yet, in minutes. Never 0 for open work. */
function minutesLeft(a: AssignmentWithArticle): number {
  const seen = a.progress ? a.progress.furthestPageIndex + 1 : 0;
  const remaining = Math.max(a.article.pageCount - seen, 0);
  return Math.max(remaining, 1) * MINUTES_PER_PAGE;
}

/** 1-based page the reader will reopen on. */
const resumePage = (a: AssignmentWithArticle): number =>
  Math.min((a.progress?.lastPageIndex ?? 0) + 1, Math.max(a.article.pageCount, 1));

const shortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function dueLabel(a: AssignmentWithArticle): { text: string; overdue: boolean } | null {
  if (!a.dueDate) return null;
  const overdue = Date.parse(a.dueDate) < Date.now();
  return { text: `${overdue ? 'Overdue' : 'Due'} ${shortDate(a.dueDate)}`, overdue };
}

/** Why this landed on the learner, in their words rather than the enum's. */
function reasonLabel(a: AssignmentWithArticle): string {
  switch (a.reason) {
    case 'indicator_deficit':
      return 'From your RSQ scores';
    case 'complaint_deficit':
      return 'From a complaint trend';
    case 'tag_rule':
      return a.sourceTag ? `Part of your ${a.sourceTag} track` : 'Part of your role track';
    default:
      return 'Assigned to you';
  }
}

const STATE_LABEL: Record<AssignmentState, string> = {
  new: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
  expired: 'Expired',
};

/* Summary strip
 * -------------------------------------------------------------------------- */

function SummaryStrip({ groups }: { groups: Groups }) {
  const total = groups.all.length;
  const completed = groups.all.filter((a) => a.state === 'completed').length;
  const open = [...groups.inProgress, ...groups.upNext];
  const minutes = open.reduce((sum, a) => sum + minutesLeft(a), 0);
  const nextDue = groups.upNext
    .concat(groups.inProgress)
    .filter((a) => a.dueDate)
    .sort((a, b) => dueMillis(a) - dueMillis(b))[0];

  const detail = [
    open.length > 0 ? `About ${minutes} min left` : 'All caught up',
    nextDue?.dueDate ? `next due ${shortDate(nextDue.dueDate)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={styles.summary}>
      <Ring
        value={total ? completed / total : 0}
        label={`${completed}/${total}`}
        className={styles.summaryRing}
      />
      <div className={styles.summaryBody}>
        <p className={styles.summaryTitle}>
          {completed} of {total} module{total === 1 ? '' : 's'} complete
        </p>
        <p className={styles.summaryDetail}>{detail}</p>
        <span className={styles.segments} aria-hidden="true">
          {groups.all.map((a) => (
            <i
              key={a.assignmentId}
              className={`${styles.segment} ${styles[`segment_${a.state}`] ?? ''}`}
              style={
                a.state === 'in_progress'
                  ? ({ '--pct': `${a.progressPct}%` } as CSSProperties)
                  : undefined
              }
            />
          ))}
        </span>
      </div>
    </div>
  );
}

/* Shared pieces
 * -------------------------------------------------------------------------- */

const RING_R = 22;
const RING_C = 2 * Math.PI * RING_R;

function Ring({ value, label, className }: { value: number; label: string; className?: string }) {
  const clamped = Math.min(Math.max(value, 0), 1);
  return (
    <span className={`${styles.ring} ${className ?? ''}`}>
      <svg viewBox="0 0 52 52" aria-hidden="true" focusable="false">
        <circle className={styles.ringTrack} cx="26" cy="26" r={RING_R} />
        <circle
          className={styles.ringFill}
          cx="26"
          cy="26"
          r={RING_R}
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - clamped)}
        />
      </svg>
      <span className={styles.ringLabel}>{label}</span>
    </span>
  );
}

function SectionHeading({
  children,
  count,
  action,
}: {
  children: string;
  count?: number;
  action?: ReactNode;
}) {
  return (
    <div className={styles.sectionRow}>
      <h2 className={styles.sectionTitle}>
        {children}
        {count != null && <span className={styles.sectionCount}> · {count}</span>}
      </h2>
      {action}
    </div>
  );
}

function ReasonPill({ assignment }: { assignment: AssignmentWithArticle }) {
  return (
    <span className={styles.pill}>
      <i
        className={styles.reasonDot}
        style={{ background: REASON_ACCENT[assignment.reason] }}
        aria-hidden="true"
      />
      {reasonLabel(assignment)}
    </span>
  );
}

/* Hero: the article the learner is in the middle of
 * -------------------------------------------------------------------------- */

interface CardProps {
  assignment: AssignmentWithArticle;
  onOpen: () => void;
}

function HeroCard({ assignment, onOpen }: CardProps) {
  const { article } = assignment;
  const page = resumePage(assignment);
  const mins = minutesLeft(assignment);
  const pct = Math.min(Math.max(assignment.progressPct, 0), 100);

  return (
    <button
      type="button"
      className={styles.hero}
      onClick={onOpen}
      aria-label={`Resume ${article.title}. Page ${page} of ${article.pageCount}, about ${mins} minutes left.`}
    >
      <CurateCover
        slug={article.slug}
        tag={article.tags[0]}
        reason={assignment.reason}
        imageUrl={article.headerImageUrl}
        hero
        className={styles.heroArt}
      />
      <span className={styles.heroScrim} aria-hidden="true" />

      <span className={styles.heroKicker}>
        <i className={styles.heroKickerDot} aria-hidden="true" />
        In progress
      </span>
      <span className={styles.heroTitle}>{article.title}</span>
      <span className={styles.heroMeta}>
        Page {page} of {article.pageCount} · about {mins} min left
      </span>
      <span className={styles.heroTrack} aria-hidden="true">
        <span className={styles.heroFill} style={{ width: `${pct}%` }} />
      </span>
      <span className={styles.heroCta}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
        Resume
      </span>
    </button>
  );
}

/* Card: one unstarted (or secondary in-progress) article
 * -------------------------------------------------------------------------- */

function AssignmentCard({ assignment, onOpen }: CardProps) {
  const { article, state } = assignment;
  const due = dueLabel(assignment);
  const mins = minutesLeft(assignment);
  const questions = article.questionCount;

  return (
    <button
      type="button"
      className={styles.card}
      onClick={onOpen}
      aria-label={`Open ${article.title}. ${STATE_LABEL[state]}, ${article.pageCount} pages, about ${mins} minutes.`}
    >
      <CurateCover
        slug={article.slug}
        tag={article.tags[0]}
        reason={assignment.reason}
        imageUrl={article.headerImageUrl}
        className={styles.cardCover}
      />

      <span className={styles.cardBody}>
        <span className={styles.cardTitle}>{article.title}</span>
        {article.subtitle && <span className={styles.cardSubtitle}>{article.subtitle}</span>}
        <span className={styles.cardMeta}>
          <span>
            {article.pageCount} page{article.pageCount === 1 ? '' : 's'}
          </span>
          {questions > 0 && (
            <span>
              {questions} question{questions === 1 ? '' : 's'}
            </span>
          )}
          <span>{mins} min</span>
        </span>
        <span className={styles.pills}>
          {state === 'in_progress' && (
            <span className={`${styles.pill} ${styles.pillProgress}`}>
              Page {resumePage(assignment)} of {article.pageCount}
            </span>
          )}
          {due && (
            <span className={`${styles.pill} ${due.overdue ? styles.pillOverdue : styles.pillDue}`}>
              {due.text}
            </span>
          )}
          <ReasonPill assignment={assignment} />
        </span>
      </span>

      <span className={styles.cardChevron} aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
      </span>
    </button>
  );
}

/* Row: finished or expired work, kept compact
 * -------------------------------------------------------------------------- */

function CompletedRow({ assignment, onOpen }: CardProps) {
  const { article, state, score } = assignment;
  const expired = state === 'expired';
  const when = assignment.completedAt ? ` ${shortDate(assignment.completedAt)}` : '';
  const detail = expired
    ? 'Expired'
    : `Completed${when}${score == null ? ' · no graded questions' : ''}`;

  return (
    <button
      type="button"
      className={`${styles.row} ${expired ? styles.rowExpired : ''}`}
      onClick={onOpen}
      aria-label={`Review ${article.title}. ${STATE_LABEL[state]}${score != null ? `, score ${score}%` : ''}.`}
    >
      <CurateCover
        slug={article.slug}
        tag={article.tags[0]}
        reason={assignment.reason}
        imageUrl={article.headerImageUrl}
        className={styles.rowCover}
      />
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>{article.title}</span>
        <span className={styles.rowDetail}>{detail}</span>
      </span>
      {!expired && score != null ? (
        <Ring value={score / 100} label={`${score}%`} className={styles.rowRing} />
      ) : (
        <span
          className={`${styles.rowCheck} ${expired ? styles.rowCheckMuted : ''}`}
          aria-hidden="true"
        >
          {expired ? (
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12l5 5L19 7" />
            </svg>
          )}
        </span>
      )}
    </button>
  );
}
